/**
 * The public booking API's shapes, plus the normalizers that produce them.
 *
 * The Worker is written by another hand and has already shipped both
 * `duration_min` and `durationMin` for the same field, so nothing here trusts
 * a single spelling: every response is narrowed at the boundary and the rest
 * of the UI only ever sees the camelCase shape below.
 */

import type { Locale } from '@/shared/i18n';
import { sanitizeThemeColors, type ThemeColors } from '@/shared/theme/themeColors';
import type { IntakeQuestion } from '@/shared/calendar/intakeTypes';

export type LocationType =
  | 'custom'
  | 'in_person'
  | 'phone'
  | 'video'
  | 'google_meet'
  | 'zoom'
  | 'teams';
export type BookingMode = 'guest' | 'account' | 'both';
export type BookingStatus =
  | 'pending'
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'no_show';

export interface PublicEventType {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string | null;
  readonly durationMin: number;
  readonly price: number | null;
  readonly currency: string;
  readonly color: string | null;
  readonly locationType: LocationType;
  readonly locationValue: string | null;
  /** Booking horizon in days. Bounds how far the calendar can page forward. */
  readonly maxBookingDays: number;
  readonly bookingType?: 'individual' | 'group';
  readonly maxAttendees?: number;
  readonly intakeQuestions?: readonly IntakeQuestion[];
}

export interface PublicPage {
  readonly slug: string;
  readonly title: string;
  readonly bio: string | null;
  readonly timezone: string;
}

export interface PublicCalendar {
  readonly page: PublicPage;
  readonly eventTypes: readonly PublicEventType[];
  readonly bookingMode: BookingMode;
  readonly locales: readonly Locale[];
  /**
   * The owning organization's palette, or `null` when it never set one.
   *
   * An end client is not a member of the professional's organization, so this
   * cannot be read from the browser — the Worker resolves it from the slug with
   * `service_role` and sends colours only. `BookingPage` injects it.
   */
  readonly theme: ThemeColors | null;
}

export interface SlotCapacity {
  readonly slot: string;
  readonly bookedAttendees: number;
  readonly maxAttendees: number;
  readonly remainingCapacity: number;
  readonly isFull: boolean;
  readonly badge: string | null;
}

export interface Availability {
  readonly timezone: string;
  readonly slots: readonly string[];
  readonly slotCapacities?: readonly SlotCapacity[];
}

export interface BookingRecord {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string | null;
  readonly title: string;
  readonly timezone: string;
  readonly status: BookingStatus;
  readonly manageToken: string | null;
  readonly pageSlug: string | null;
  readonly pageTitle: string | null;
  readonly eventTypeId: string | null;
  readonly durationMin: number | null;
  readonly locationType: LocationType | null;
  readonly locationValue: string | null;
  readonly notes: string | null;
  readonly intakeAnswers?: Record<string, unknown> | null;
  readonly attendeeCount?: number | null;
  readonly meetingUrl?: string | null;
  readonly meetingDetails?: Record<string, unknown> | null;
}

/* ------------------------------------------------------------------ narrow */

type Bag = Record<string, unknown>;

export function asRecord(value: unknown): Bag | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Bag)
    : null;
}

/** First key that holds a non-empty string. */
function pickString(bag: Bag, ...keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = bag[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

/** First key that holds a finite number (numeric strings included — `numeric`
 *  columns arrive from PostgREST as strings). */
function pickNumber(bag: Bag, ...keys: readonly string[]): number | null {
  for (const key of keys) {
    const value = bag[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

/** PostgREST embeds can arrive as an object or a one-element array. */
function pickEmbedded(bag: Bag, ...keys: readonly string[]): Bag | null {
  for (const key of keys) {
    const value = bag[key];
    if (Array.isArray(value)) {
      const first = asRecord(value[0]);
      if (first) return first;
    }
    const record = asRecord(value);
    if (record) return record;
  }
  return null;
}

const LOCATION_TYPES: readonly LocationType[] = [
  'custom',
  'in_person',
  'phone',
  'video',
  'google_meet',
  'zoom',
  'teams',
];

function toLocationType(value: string | null): LocationType {
  return LOCATION_TYPES.find((entry) => entry === value) ?? 'custom';
}

const STATUSES: readonly BookingStatus[] = [
  'pending',
  'confirmed',
  'completed',
  'cancelled',
  'no_show',
];

function toStatus(value: string | null): BookingStatus {
  return STATUSES.find((entry) => entry === value) ?? 'pending';
}

const LOCALES: readonly Locale[] = ['en', 'es', 'pt'];

function toLocales(value: unknown): readonly Locale[] {
  if (!Array.isArray(value)) return LOCALES;
  const found = value.filter((entry): entry is Locale =>
    LOCALES.some((locale) => locale === entry),
  );
  return found.length > 0 ? found : LOCALES;
}

function toBookingMode(value: string | null): BookingMode {
  return value === 'account' || value === 'both' || value === 'guest'
    ? value
    : 'guest';
}

export function normalizeEventType(value: unknown): PublicEventType | null {
  const bag = asRecord(value);
  if (!bag) return null;
  const id = pickString(bag, 'id');
  const name = pickString(bag, 'name', 'title');
  if (!id || !name) return null;
  return {
    id,
    slug: pickString(bag, 'slug', 'event_slug', 'eventSlug') ?? id,
    name,
    description: pickString(bag, 'description'),
    durationMin: pickNumber(bag, 'durationMin', 'duration_min') ?? 30,
    price: pickNumber(bag, 'price'),
    currency: pickString(bag, 'currency') ?? 'USD',
    color: pickString(bag, 'color'),
    locationType: toLocationType(
      pickString(bag, 'locationType', 'location_type'),
    ),
    locationValue: pickString(bag, 'locationValue', 'location_value'),
    maxBookingDays:
      pickNumber(bag, 'maxBookingDays', 'max_booking_days') ?? 60,
    bookingType: (pickString(bag, 'bookingType', 'booking_type') === 'group' ? 'group' : 'individual') as 'individual' | 'group',
    maxAttendees: pickNumber(bag, 'maxAttendees', 'max_attendees') ?? 1,
    intakeQuestions: Array.isArray(bag.intakeQuestions) ? bag.intakeQuestions : (Array.isArray(bag.intake_questions) ? bag.intake_questions : []),
  };
}

export function normalizeCalendar(value: unknown): PublicCalendar | null {
  const bag = asRecord(value);
  if (!bag) return null;
  const pageBag = asRecord(bag.page) ?? bag;
  const slug = pickString(pageBag, 'slug');
  if (!slug) return null;

  const rawEvents = Array.isArray(bag.eventTypes)
    ? bag.eventTypes
    : Array.isArray(bag.event_types)
      ? bag.event_types
      : [];

  const eventTypes = rawEvents
    .map(normalizeEventType)
    .filter((entry): entry is PublicEventType => entry !== null);

  return {
    page: {
      slug,
      title: pickString(pageBag, 'title', 'name') ?? slug,
      bio: pickString(pageBag, 'bio', 'description'),
      timezone: pickString(pageBag, 'timezone', 'time_zone') ?? 'UTC',
    },
    eventTypes,
    bookingMode: toBookingMode(
      pickString(bag, 'bookingMode', 'booking_mode') ??
        pickString(pageBag, 'bookingMode', 'booking_mode'),
    ),
    locales: toLocales(bag.locales ?? pageBag.locales),
    theme: normalizeTheme(bag.theme ?? pageBag.theme),
  };
}

/**
 * The Worker already filtered the payload down to `#rrggbb` values, but it is
 * still a wire shape: `sanitizeThemeColors` is what decides which keys are real
 * theme roles. An empty result becomes `null` — "inject nothing", not "inject
 * an empty palette".
 */
function normalizeTheme(value: unknown): ThemeColors | null {
  const colors = sanitizeThemeColors(value);
  return Object.keys(colors).length > 0 ? colors : null;
}

export function normalizeAvailability(value: unknown): Availability | null {
  const bag = asRecord(value);
  if (!bag || !Array.isArray(bag.slots)) return null;

  const rawCapacities = Array.isArray(bag.slotCapacities)
    ? bag.slotCapacities
    : Array.isArray(bag.slot_capacities)
      ? bag.slot_capacities
      : undefined;

  const slotCapacities: SlotCapacity[] | undefined = rawCapacities
    ? rawCapacities.map((c) => {
        const cBag = asRecord(c) ?? {};
        return {
          slot: pickString(cBag, 'slot') ?? '',
          bookedAttendees: pickNumber(cBag, 'bookedAttendees', 'booked_attendees') ?? 0,
          maxAttendees: pickNumber(cBag, 'maxAttendees', 'max_attendees') ?? 1,
          remainingCapacity: pickNumber(cBag, 'remainingCapacity', 'remaining_capacity') ?? 0,
          isFull: Boolean(cBag.isFull ?? cBag.is_full),
          badge: pickString(cBag, 'badge'),
        };
      })
    : undefined;

  return {
    timezone: pickString(bag, 'timezone', 'time_zone') ?? 'UTC',
    slots: bag.slots.filter(
      (slot): slot is string =>
        typeof slot === 'string' && Number.isFinite(Date.parse(slot)),
    ),
    ...(slotCapacities ? { slotCapacities } : {}),
  };
}

export function normalizeBooking(value: unknown): BookingRecord | null {
  const outer = asRecord(value);
  if (!outer) return null;
  const bag = asRecord(outer.booking) ?? outer;
  const id = pickString(bag, 'id');
  const startsAt = pickString(bag, 'startsAt', 'starts_at');
  if (!id || !startsAt) return null;

  const page = pickEmbedded(bag, 'calendar_pages', 'page', 'calendarPage');
  const event = pickEmbedded(
    bag,
    'calendar_event_types',
    'eventType',
    'calendarEventType',
  );

  return {
    id,
    startsAt,
    endsAt: pickString(bag, 'endsAt', 'ends_at'),
    title:
      pickString(bag, 'title', 'eventName', 'event_name') ??
      (event ? (pickString(event, 'name', 'title') ?? '') : ''),
    timezone:
      pickString(bag, 'timezone', 'time_zone') ??
      (page ? pickString(page, 'timezone', 'time_zone') : null) ??
      'UTC',
    status: toStatus(pickString(bag, 'status')),
    manageToken: pickString(bag, 'manageToken', 'manage_token'),
    pageSlug:
      pickString(bag, 'pageSlug', 'page_slug') ??
      (page ? pickString(page, 'slug') : null),
    pageTitle:
      pickString(bag, 'pageTitle', 'page_title') ??
      (page ? pickString(page, 'title') : null),
    eventTypeId: pickString(bag, 'eventTypeId', 'event_type_id'),
    durationMin:
      pickNumber(bag, 'durationMin', 'duration_min') ??
      (event ? pickNumber(event, 'durationMin', 'duration_min') : null),
    locationType: (() => {
      const raw =
        pickString(bag, 'locationType', 'location_type') ??
        (event ? pickString(event, 'locationType', 'location_type') : null);
      return raw === null ? null : toLocationType(raw);
    })(),
    locationValue:
      pickString(bag, 'locationValue', 'location_value') ??
      (event ? pickString(event, 'locationValue', 'location_value') : null),
    notes: pickString(bag, 'notes'),
    intakeAnswers: asRecord(bag.intakeAnswers ?? bag.intake_answers) ?? null,
    attendeeCount: pickNumber(bag, 'attendeeCount', 'attendee_count'),
    meetingUrl: pickString(bag, 'meetingUrl', 'meeting_url'),
    meetingDetails: asRecord(bag.meetingDetails ?? bag.meeting_details) ?? null,
  };
}

export function normalizeBookings(value: unknown): BookingRecord[] {
  const bag = asRecord(value);
  const list = Array.isArray(bag?.bookings)
    ? bag.bookings
    : Array.isArray(value)
      ? value
      : [];
  return list
    .map((entry) => normalizeBooking(entry))
    .filter((entry): entry is BookingRecord => entry !== null);
}
